import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import DWLFMarketChart from '../../components/DWLFMarketChart';
import SymbolSidebar from './components/SymbolSidebar';
import DeskPanel from './desk/DeskPanel';
import useCycleClockData from './clock/useCycleClockData';
import {
  buildWeeklyCycleLowSettings,
  buildWeeklyCycleHighSettings,
} from '@andywilliams/indicators';
import { getSystemSettings, recomputeCycles } from '../../api/admin';
import { extractErrorMessage } from '../../api/extractErrorMessage';
import { eventsAPI } from '../../api/events';
import { customEventsApi } from '../../api/customEvents';
import {
  loadMarketData,
  loadOlderHistory,
  fetchSupportResistance,
  fetchTrendlines,
  fetchSwingPoints,
  fetchSymbolDetails,
  fetchWeeklyTrendlines,
  fetchWeeklySwingPoints,
  fetchAlertsByAsset,
  fetchSystemFibLevels,
  fetchMarketSymbols,
  fetchTags,
  fetchAssetsByTag,
  fetchWatchlist,
  DEFAULT_TRENDLINE_VERSION,
} from './marketsSlice';
import {
  fetchUserSettings,
  USER_SETTING_SEMI_LIVE_CANDLES,
  selectUserSettingsSynced,
} from '../userSettings/userSettingsSlice';
import useMarketSignals from './hooks/useMarketSignals';
import useChartPreferences from './hooks/useChartPreferences';
import useNewSaveError from './hooks/useNewSaveError';
// TIMEFRAME_UI_TO_CANONICAL maps the UI's long-form timeframe key
// ('weekly'/'daily'/'hourly') onto the canonical short-form ('1w'/'1d'/'1h')
// the backend requires — CycleStateTable rows are keyed in that vocabulary and
// the endpoint rejects long-forms at the boundary. The map itself lives in
// utils/timeframes and is re-exported by the hook; imported rather than
// redeclared, because two copies silently diverge the day a timeframe is added.
import useBackendCycleEvents, { TIMEFRAME_UI_TO_CANONICAL } from './hooks/useBackendCycleEvents';
import useBackendKeyLevelEvents from './hooks/useBackendKeyLevelEvents';
import useBackendCycleFibEvents from './hooks/useBackendCycleFibEvents';
import useBackendDivergenceEvents from './hooks/useBackendDivergenceEvents';
import useCycleWindows from './hooks/useCycleWindows';
import usePastCycleWindows from './hooks/usePastCycleWindows';
import useCycleWindowState from './hooks/useCycleWindowState';
import useStage from './hooks/useStage';
import {
  bandTimeframe, chartCycleProps, describeCycleStatus, describePastWindows, isDrawableWindow, isStoreTimeframe,
} from './cycleWindowBand';
import usePermissions from '../../hooks/usePermissions';
import CycleTuningPanel from './cycleTuning/CycleTuningPanel';
import DWLFLoader from '../../components/DWLFLoader';
import useStaleDataRefresh from '../../hooks/useStaleDataRefresh';
import useContextualPopover from '../../hooks/useContextualPopover';
import ContextualPopover from '../../components/ContextualPopover/ContextualPopover';
import {
  toTimestamp,
  stripLegacyTrendlineVersionPreferences,
  migrateCycleWindowPreferences,
  normalizeSymbolKey,
  createSymbolVariants,
  timeframeOptionsFor,
  fallbackTimeframe,
} from './marketDetailUtils';
import { rememberChart } from '../../utils/lastChart';
import StageStrip from './desk/StageStrip';
import ChartStatusInfo from './ChartStatusInfo';
import SidebarToggle, { SIDEBAR_RAIL, readSidebarCollapsed, writeSidebarCollapsed } from './desk/SidebarToggle';


// Did a read behind the cycle status line fail (pivots, windows, or a window's depth band)? Drives the notes
// marker's amber state from the data, not from the sentence's wording.
const DEPTH_READ_FAILURES = new Set(['read-failed', 'internal-error']);
const cycleStatusFailed = ({ backendCycles, cycleWindows }) => Boolean(backendCycles?.error)
  || Boolean(cycleWindows?.error)
  // a depth-read failure counts only where its zone note is written: on a drawn window
  || [cycleWindows?.windows?.low, cycleWindows?.windows?.high]
    .some((w) => isDrawableWindow(w) && DEPTH_READ_FAILURES.has(w?.depthUnavailable));

const MarketDetail = () => {
  const { symbol } = useParams();
  useEffect(() => { rememberChart(symbol); }, [symbol]);
  // Watchlist stages for the rail's dots: the Cycle Clock's model and (cached) data (DWLF-300).
  const { markets: clockMarkets } = useCycleClockData();
  const stagesBySymbol = useMemo(
    () => Object.fromEntries((clockMarkets || []).map((m) => [m.symbol, m.stage])),
    [clockMarkets],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { hasPermission, isAdmin } = usePermissions();
  // DWLF-184: the Trendlines V3 layer is an admin beta while it soaks.
  const cycleTrendlinesBeta = isAdmin();
  const canTuneSystemSettings = hasPermission('system:settings:write');
  // Gated on the permission the ROUTE enforces, not on admin:*. The two had to
  // agree once the endpoint moved to cycles:rebuild:run — and admin:* does not
  // match it (permissionMatches expands the prefix to 'admin:'), so an admin:*
  // gate here would offer a button the API refuses. It also makes delegation real
  // in the product: a non-admin role granted this one permission sees the button,
  // which an admin-only gate would have limited to API keys.
  const canRebuildCycles = hasPermission('cycles:rebuild:run');
  const [cycleTuningOpen, setCycleTuningOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [, setHighlightedPatterns] = useState([]);
  const [tradeSignalType, setTradeSignalType] = useState('system'); // 'system' or 'user'
  const [selectedStrategy, setSelectedStrategy] = useState('all'); // 'all' or specific strategy
  const [selectedTimeframe, setSelectedTimeframe] = useState('daily');
  const [pendingPreferredTimeframe, setPendingPreferredTimeframe] =
    useState(null);
  const [customEvents, setCustomEvents] = useState([]);
  const [customEventDefinitions, setCustomEventDefinitions] = useState([]);
  // True once the definitions fetch has completed (success OR failure). The
  // chart uses this to tell "definitions still loading" (don't touch saved
  // selections) apart from "loaded and genuinely empty" (prune selections for
  // deleted events so the count/badge/markers clear).
  const [customEventDefinitionsLoaded, setCustomEventDefinitionsLoaded] = useState(false);
  // Custom-event ids the user has toggled ON in the chart's "My Data" menu.
  // The chart reports these up via onSelectedCustomEventIdsChange so we fetch
  // fires for ONLY those events (scoped by customEventId), not all of them.
  const [selectedCustomEventIds, setSelectedCustomEventIds] = useState([]);
  const handleSelectedCustomEventIdsChange = useCallback((ids) => {
    const next = Array.isArray(ids) ? ids : [];
    setSelectedCustomEventIds((prev) => {
      if (prev.length === next.length) {
        const a = [...prev].sort();
        const b = [...next].sort();
        if (a.every((v, i) => v === b[i])) {
          return prev; // unchanged — avoid a redundant refetch
        }
      }
      return next;
    });
  }, []);
  const [weeklyCycleLowSettings, setWeeklyCycleLowSettings] = useState(null);
  const [weeklyCycleHighSettings, setWeeklyCycleHighSettings] = useState(null);
  // Live-preview tuning: local overrides for the weekly cycle low/high
  // settings on the current symbol. When set, the chart's re-detection reads
  // these instead of `weeklyCycleLowSettings` / `weeklyCycleHighSettings`,
  // so markers move in real time without persisting anything. Nulled out
  // after Save (which refreshes the persisted state) or Discard.
  const [localCycleLowSettings, setLocalCycleLowSettings] = useState(null);
  const [localCycleHighSettings, setLocalCycleHighSettings] = useState(null);
  // Admin "Recompute now" controls: in-flight flag drives the button's
  // disabled state, snackbar surfaces success/error feedback. Recompute
  // calls POST /v2/admin/cycles/recompute → backend deletes the
  // CycleStateTable row(s) AND this scope's EventsTable rows for the current
  // (symbol, timeframe), and the next cron tick re-runs the batch detector
  // against full candle history with the latest saved settings. The
  // EventsTable half is why this needs an acknowledgement — see the dialog
  // below. Used after a cycle-settings tune-save
  // when the admin wants the backend chart to reflect the new settings
  // immediately rather than waiting for a global CYCLE_SEED_VERSION bump.
  const [recomputeInFlight, setRecomputeInFlight] = useState(false);
  // (DWLF-104) Recompute bypasses the cycle ledger: it deletes this scope's
  // EventsTable rows and rebuilds them, and a deleted row cannot carry
  // `revoked` — so every pivot the ledger has retired here comes back visible.
  // Since consumers began filtering `revoked` (DWLF-110) that is no longer
  // cosmetic. The backend refuses without an explicit acknowledgement; this
  // dialog IS that acknowledgement. Sending the flag silently from the API
  // layer would defeat the guard entirely — the point is that a human sees
  // what they are about to undo.
  // Holds the SCOPE that was acknowledged, not just "a dialog is open". The
  // admin confirms a specific (symbol, timeframe) after reading it, and
  // selectedTimeframe can change underneath an open dialog with no user action
  // on it — a late fetchSymbolDetails whose supported set drops the current
  // timeframe reassigns it. Re-reading at click time would rebuild a scope
  // nobody agreed to. null when closed.
  const [bypassConfirmScope, setBypassConfirmScope] = useState(null);
  const [recomputeSnack, setRecomputeSnack] = useState({ open: false, severity: 'success', message: '' });
  // Token that cycle-settings load effects can watch to refresh after a Save.
  const [cycleSettingsReloadToken, setCycleSettingsReloadToken] = useState(0);
  // Per-side generation counters bumped after each cycle-settings load
  // attempt completes (success OR failure). The tuning panel watches both
  // to detect that the round-trip for the current timeframe has finished.
  // We track the two sides separately so consumers can wait for BOTH
  // loaders to settle — re-enabling editing after just one would risk a
  // save that mixes new-TF lows with previous-TF highs (or vice versa).
  // The counter approach is required because `setWeeklyCycleLowSettings(null)`
  // after a previous null state is a React no-op and wouldn't otherwise
  // signal completion via the data refs.
  const [cycleLowLoadGeneration, setCycleLowLoadGeneration] = useState(0);
  const [cycleHighLoadGeneration, setCycleHighLoadGeneration] = useState(0);

  // Annotation panel state
  const [chartAnnotations, setChartAnnotations] = useState([]);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const annotationChartRef = useRef(null);

  // Sidebar state (lifted here to survive early returns)
  const [sidebarTab, setSidebarTab] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const toggleSidebar = useCallback(() => setSidebarCollapsed((c) => !c), []);
  // Folding is a desktop layout: below `lg` (where the toggle isn't shown) the column is never folded.
  const sidebarFolded = sidebarCollapsed && !isMobile;
  useEffect(() => { writeSidebarCollapsed(sidebarCollapsed); }, [sidebarCollapsed]);
  const [collapsedAnnotationTypes, setCollapsedAnnotationTypes] = useState(new Set());

  // Contextual popover for first chart view
  const chartPopover = useContextualPopover('first-chart-view');
  const symbolSidebarRef = useRef(null);

  // Signal and trade data hooks
  const { signals, userSignals, trades, tradeNotes, loading: signalsLoading, refresh: refreshSignals } = useMarketSignals(symbol);

  const symbolKeyVariants = useMemo(() => {
    if (!symbol) {
      return [];
    }

    const hyphenated = symbol.replace('/', '-');
    const slashed = symbol.replace('-', '/');

    return Array.from(new Set([symbol, hyphenated, slashed].filter(Boolean)));
  }, [symbol]);

  const formattedSymbol = useMemo(
    () => (symbol ? symbol.replace('/', '-') : ''),
    [symbol],
  );

  useEffect(() => {
    // Abandon any in-flight local tuning when the user navigates to a
    // different symbol or switches timeframe. Keeping the overrides around
    // would misapply the previous symbol/TF's tweaks to the new chart and
    // mark the tuning panel dirty against settings it never edited.
    // Note: depends on selectedTimeframe (raw) rather than normalizedTimeframeKey
    // because the normalised value is declared further down the component
    // and would TDZ-error if referenced here.
    setLocalCycleLowSettings(null);
    setLocalCycleHighSettings(null);
  }, [formattedSymbol, selectedTimeframe]);

  const pickFromCollection = useCallback(
    (collection, fallback) => {
      if (!collection) {
        return fallback;
      }

      for (const key of symbolKeyVariants) {
        if (Object.prototype.hasOwnProperty.call(collection, key)) {
          return collection[key];
        }
      }

      return fallback;
    },
    [symbolKeyVariants],
  );

  // Select data from Redux store
  const marketData =
    useSelector((state) => pickFromCollection(state.markets.marketData, {})) ||
    {};
  const supportResistanceByTimeframe = useSelector((state) =>
    pickFromCollection(state.markets.supportResistance, null),
  );
  const trendlineState = useSelector((state) =>
    pickFromCollection(state.markets.trendlines, null),
  );
  const symbolDetails = useSelector((state) =>
    pickFromCollection(state.markets.symbolDetails, {}),
  );
  const weeklyTrendlineState = useSelector((state) =>
    pickFromCollection(state.markets.weeklyTrendlines, null),
  );
  const swingPointsState = useSelector((state) =>
    pickFromCollection(state.markets.swingPoints, null),
  );
  const weeklySwingPoints = useSelector((state) =>
    pickFromCollection(state.markets.weeklySwingPoints, []),
  );
  const alerts = useSelector((state) =>
    pickFromCollection(state.markets.alertsByAsset, []),
  );
  const symbols = useSelector((state) => state.markets.symbols || []);
  const tagsList = useSelector((state) => state.markets.tags || []);
  const assetsByTagMap = useSelector(
    (state) => state.markets.assetsByTag || {},
  );
  const watchlist = useSelector((state) => state.markets.watchlist || []);
  const status = useSelector((state) => state.markets.status);
  const symbolsStatus = useSelector((state) => state.markets.symbolsStatus);
  const symbolsError = useSelector((state) => state.markets.symbolsError);
  const tagsLoaded = Array.isArray(tagsList) && tagsList.length > 0;
  const rawFibLevels = useSelector((state) => state.markets.fibLevels[symbol]) || [];
  const fibLevels = useMemo(
    () => (Array.isArray(rawFibLevels) ? rawFibLevels : []),
    [rawFibLevels],
  );
  const hasFibLevels = fibLevels.length > 0;
  const activeFibLevels = fibLevels;
  const autoShowFib = hasFibLevels;
  const {
    settings: userSettings = {},
    loaded: userSettingsLoaded,
    loading: userSettingsLoading,
    saveError: userSettingsSaveError,
  } = useSelector((state) => state.userSettings);
  const userSettingsSynced = useSelector(selectUserSettingsSynced);
  const [saveErrorOpen, closeSaveError] = useNewSaveError(userSettingsSaveError);
  
  // Semi-live candles setting
  const semiLiveCandlesEnabled = userSettings?.[USER_SETTING_SEMI_LIVE_CANDLES] !== false;

  // Auto-refresh stale chart data (>1 hour old)
  // This hook handles: visibility change, periodic checks, and on-mount staleness detection
  useStaleDataRefresh(formattedSymbol, selectedTimeframe);

  // Stable callback to reset pending timeframe (avoids infinite re-render loop)
  const handleResetPendingTimeframe = useCallback(() => setPendingPreferredTimeframe(null), []);

  // Chart preferences hook
  const chartPrefs = useChartPreferences({
    symbol: formattedSymbol,
    selectedTimeframe,
    userSettings,
    userSettingsLoading,
    userSettingsSynced,
    tradeSignalType,
    selectedStrategy,
    pendingPreferredTimeframe,
    onResetPendingTimeframe: handleResetPendingTimeframe,
  });

  // Destructure for convenience
  const {
    preferences: chartPreferences,
    setPreference: updateChartPreferences,
    isDirty: chartPreferencesDirty,
    markAsReady: setAppliedChartPreferencesSymbol,
    axisColors,
    crosshairSnapMode,
    showCrosshairPriceLabel,
    showAdaptiveGrid,
    showAdminChartControls,
    chartPreferencesReadyForSymbol,
    savedChartPreferences,
    showDailyTrendlines,
    showWeeklyTrendlines,
    showHourlyTrendlines,
    onChartPreferencesChange: handleChartPreferencesChange,
    onToggleWeeklyTrendlines: handleToggleWeeklyTrendlines,
    onToggleDailyTrendlines: handleToggleDailyTrendlines,
    onToggleHourlyTrendlines: handleToggleHourlyTrendlines,
  } = chartPrefs;

  // Tune button is admin-only and hidden when the user has turned every
  // cycle marker off in the Cycles menu — if nothing is rendered there's
  // nothing to tune against. Absent preference keys treat as "on" so the
  // button shows by default before prefs load.
  //
  // The `showWeeklyCycle…` preference names are leftover from when cycles
  // were weekly-only; since Phase 1 these toggles apply to whatever
  // timeframe the chart is currently on. Renaming the preference keys
  // themselves is a separate cleanup (touches DWLFMarketChart toggles +
  // any persisted user prefs).
  const anyCycleOverlayOn = chartPreferences.showWeeklyCycleLows !== false
    || chartPreferences.showWeeklyCycleHighs !== false
    || chartPreferences.showLiveWeeklyCycleLows !== false
    || chartPreferences.showLiveWeeklyCycleHighs !== false
    || chartPreferences.showCycleLowWindow !== false
    || chartPreferences.showCycleHighWindow !== false;
  // Admin-only Tune + Recompute buttons are off by default now that
  // backend-sourced cycles match the client for day-to-day viewing.
  // Turn on via User Settings → Chart Settings → Admin Chart Controls
  // before a tuning pass, then back off to keep the chart uncluttered.
  const tuneButtonVisible = canTuneSystemSettings
    && !cycleTuningOpen
    && anyCycleOverlayOn
    && showAdminChartControls;
  // Named once and used by BOTH the button and the toolbar that contains it. The
  // container renders only when something inside it will — and it used to ask only
  // about Tune, which needs `system:settings:write`. So a role granted
  // cycles:rebuild:run alone had the button gated correctly and the toolbar around
  // it never rendered, which is the delegation case this exists to enable.
  // `!cycleTuningOpen` matches what shipped: the button's own condition never
  // named it, but the toolbar only rendered when Tune did, so opening the panel
  // hid Recompute too. Keeping that — this change is about WHO sees the button,
  // not when.
  const recomputeButtonVisible = canRebuildCycles
    && !cycleTuningOpen
    && anyCycleOverlayOn
    && showAdminChartControls;

  // Validates FIRST, then opens the acknowledgement dialog. Asking someone to
  // confirm a destructive action and only then telling them it was never
  // available for this timeframe is the wrong order.
  const handleRecomputeCycles = useCallback(() => {
    const tfKey = (selectedTimeframe || '').toLowerCase();
    const canonicalTimeframe = TIMEFRAME_UI_TO_CANONICAL[tfKey];
    // Name the actual cause. Blaming the timeframe when the symbol is what
    // failed to resolve sends the admin looking at the wrong control.
    if (!formattedSymbol || !canonicalTimeframe) {
      setRecomputeSnack({
        open: true,
        severity: 'error',
        message: formattedSymbol
          ? `Recompute not available for timeframe "${tfKey}".`
          : 'Recompute not available — no symbol resolved yet.',
      });
      return;
    }
    setBypassConfirmScope({ symbol: formattedSymbol, tfKey, canonicalTimeframe });
  }, [formattedSymbol, selectedTimeframe]);

  // Drop the acknowledgement when the page moves off the symbol it was written
  // about — in flight or not. A dialog naming a symbol the admin has already
  // navigated away from asks them to agree to something under a heading that no
  // longer matches, and an acknowledgement is worth exactly what its reader was
  // looking at. Closing mid-flight costs nothing: the request carries the
  // snapshotted scope, and the result arrives in a snackbar that names it.
  useEffect(() => {
    setBypassConfirmScope(null);
  }, [formattedSymbol]);

  // Sends the request, against the scope the admin ACKNOWLEDGED rather than
  // whatever the page holds now — that is the whole point of snapshotting it.
  // The dialog stays up until the request settles (closed in `finally`), so
  // the in-flight state is something the admin actually sees.
  const handleRecomputeConfirmed = useCallback(async () => {
    const scope = bypassConfirmScope;
    // Unreachable — the dialog only renders when a scope exists. Kept because
    // this handler must be correct on its own, not merely unreachable, and
    // loud because a confirm click that does nothing is the worst outcome on
    // a destructive path.
    if (!scope) {
      setRecomputeSnack({
        open: true,
        severity: 'error',
        message: 'Recompute could not run — the confirmed scope was lost.',
      });
      return;
    }
    const { symbol: scopedSymbol, canonicalTimeframe } = scope;
    setRecomputeInFlight(true);
    try {
      // Omitting `side` triggers both lows and highs — the typical case
      // after a tune-save, where the user has changed settings that
      // affect both.
      const result = await recomputeCycles({
        symbol: scopedSymbol,
        timeframe: canonicalTimeframe,
        // The admin has just been shown what this undoes and confirmed it.
        acknowledgeLedgerBypass: true,
      });
      const sidesCleared = Array.isArray(result?.deleted) ? result.deleted.length : 0;
      // The endpoint's blast radius is CycleStateTable rows AND the scope's
      // EventsTable rows, but only the first was ever reported — so the amount
      // of event data destroyed went unrecorded anywhere the admin would see
      // it. `eventsDeleted` is the number of retired pivots about to reappear;
      // it is the figure that makes the bypass concrete after the fact.
      const eventsDeleted = Number.isFinite(result?.eventsDeleted) ? result.eventsDeleted : null;
      const scale = eventsDeleted === null
        ? `${sidesCleared} sides cleared`
        : `${sidesCleared} sides, ${eventsDeleted} stored events deleted`;
      // Surfaced when the audit row did NOT land: that row is the only record
      // the bypass happened, so its absence is the operator's cue to write it
      // down themselves. Silence here would hide the one thing they must act on.
      const auditNote = result?.bypassRecorded === false
        ? ' ⚠️ The bypass audit row was NOT recorded — note this bypass manually.'
        : '';
      setRecomputeSnack({
        open: true,
        // Warning, not success, when the audit row did not land: the operation
        // succeeded but its accountability half did not, and those should not
        // look the same.
        severity: auditNote ? 'warning' : 'success',
        // Carries the ledger follow-up through to the last thing the admin
        // sees. The dialog says the repair is only half-done, but by now that
        // text is gone and this is the moment the second step becomes
        // actionable — and the whole premise of DWLF-104 is that the rebuilt
        // rows cannot carry `revoked` until the ledger re-asserts it.
        message: `Recompute scheduled for ${scopedSymbol} ${canonicalTimeframe} (${scale}). `
          + 'Next cron run will re-backfill — re-run the ledger for this scope afterwards '
          + `to re-assert revocation.${auditNote}`,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[recomputeCycles] failed', err);
      setRecomputeSnack({
        open: true,
        severity: 'error',
        message: `Recompute failed: ${extractErrorMessage(err, 'unknown error')}`,
      });
    } finally {
      setRecomputeInFlight(false);
      setBypassConfirmScope(null);
    }
  }, [bypassConfirmScope]);

  const requestedTrendlineTimeframesRef = useRef(new Set());
  const requestedTimeframesRef = useRef(new Set());
  const previousTimeframeRef = useRef(null);
  const requestedAssetsByTagRef = useRef(new Set());
  const queryParamsConsumedRef = useRef(false);
  const dateScrolledRef = useRef(false);
  const timeframeFromUrlRef = useRef(false);

  // Reset refs on symbol change to enable repeat navigation from dialog
  useEffect(() => {
    queryParamsConsumedRef.current = false;
    dateScrolledRef.current = false;
    timeframeFromUrlRef.current = false;
  }, [symbol]);

  useEffect(() => {
    requestedTrendlineTimeframesRef.current = new Set();
    requestedTimeframesRef.current = new Set();
  }, [symbol]);

  useEffect(() => {
    if (!Array.isArray(symbols) || symbols.length === 0) {
      dispatch(fetchMarketSymbols());
    }
  }, [dispatch, symbols]);

  useEffect(() => {
    if (!Array.isArray(watchlist) || watchlist.length === 0) {
      dispatch(fetchWatchlist());
    }
  }, [dispatch, watchlist]);

  useEffect(() => {
    if (!tagsLoaded) {
      dispatch(fetchTags());
    }
  }, [dispatch, tagsLoaded]);

  useEffect(() => {
    if (!Array.isArray(tagsList) || tagsList.length === 0) {
      return;
    }

    tagsList.forEach((tag) => {
      if (!tag || typeof tag !== 'string') {
        return;
      }

      if (!assetsByTagMap?.[tag] && !requestedAssetsByTagRef.current.has(tag)) {
        requestedAssetsByTagRef.current.add(tag);
        dispatch(fetchAssetsByTag(tag));
      }
    });
  }, [dispatch, tagsList, assetsByTagMap]);

  useEffect(() => {
    if (!userSettingsLoaded && !userSettingsLoading) {
      dispatch(fetchUserSettings());
    }
  }, [dispatch, userSettingsLoaded, userSettingsLoading]);

  const title = `${symbolDetails.name} (${symbol})`;
  const normalizedTimeframeKey = (selectedTimeframe || '').toLowerCase();

  // Pick S/R levels for the current timeframe
  const supportResistance = useMemo(() => {
    if (!supportResistanceByTimeframe) {return null;}
    return supportResistanceByTimeframe[normalizedTimeframeKey] || supportResistanceByTimeframe.daily || null;
  }, [supportResistanceByTimeframe, normalizedTimeframeKey]);

  const chartCandles = useMemo(() => {
    const mapCandles = marketData?.byTimeframe?.[selectedTimeframe];
    if (Array.isArray(mapCandles) && mapCandles.length > 0) {
      return mapCandles;
    }

    const latestTimeframe = marketData?.latestTimeframe;
    if (latestTimeframe === selectedTimeframe) {
      const latestCandles = marketData?.byTimeframe?.[latestTimeframe];
      if (Array.isArray(latestCandles) && latestCandles.length > 0) {
        return latestCandles;
      }
    }

    if (Array.isArray(marketData?.candles)) {
      return marketData.candles;
    }

    return [];
  }, [marketData, selectedTimeframe]);

  const customEventLookbackDays = useMemo(() => {
    if (!Array.isArray(chartCandles) || chartCandles.length < 2) {
      return 365;
    }
    const first = chartCandles[0];
    const last = chartCandles[chartCandles.length - 1];
    const firstTs = toTimestamp(first?.t ?? first?.timestamp ?? first?.time ?? first?.date);
    const lastTs = toTimestamp(last?.t ?? last?.timestamp ?? last?.time ?? last?.date);
    if (!Number.isFinite(firstTs) || !Number.isFinite(lastTs) || lastTs <= firstTs) {
      return 365;
    }
    const diffDays = Math.ceil((lastTs - firstTs) / 86_400_000) + 1;
    return Math.min(Math.max(diffDays, 1), 2000);
  }, [chartCandles]);


  // Fetch fires for ONLY the custom events toggled on in the chart's "My Data"
  // menu, scoped by customEventId so each event's full history comes back
  // (sparse → a page or two) instead of all custom events sharing one 200-row
  // page — which silently dropped older fires once a user had many custom
  // events. Re-runs on selection / lookback-window / symbol change. Pagination
  // uses `cursor` (the backend's field); the old loop read `nextToken`, which
  // never advanced, so only the first page ever loaded.
  useEffect(() => {
    if (!formattedSymbol) {
      return;
    }
    if (!selectedCustomEventIds || selectedCustomEventIds.length === 0) {
      setCustomEvents([]);
      return;
    }

    let isActive = true;
    const fetchSelectedCustomEvents = async () => {
      try {
        const symbols = symbolKeyVariants.length > 1 ? symbolKeyVariants : [formattedSymbol];
        // allSettled, not all: the fan-out issues one request-chain per
        // selected event, so a single transient failure must not reject the
        // whole batch and blank the markers for events that loaded fine.
        const settled = await Promise.allSettled(
          selectedCustomEventIds.map(async (customEventId) => {
            const events = [];
            let cursor = null;
            let iterations = 0;
            do {
              // eslint-disable-next-line no-await-in-loop
              const response = await eventsAPI.getRecentEvents({
                scope: 'user',
                eventTypes: ['custom_event'],
                customEventId,
                symbols: symbols.length > 1 ? symbols : undefined,
                symbol: symbols.length === 1 ? symbols[0] : undefined,
                days: customEventLookbackDays,
                limit: 200,
                cursor: cursor || undefined,
              });
              events.push(...(response.events || []));
              cursor = response.cursor || null;
              iterations += 1;
            } while (cursor && iterations < 10);
            return events;
          }),
        );

        if (isActive) {
          const failed = settled.filter((r) => r.status === 'rejected');
          if (failed.length > 0) {
            console.warn(
              `Custom-event fetch failed for ${failed.length}/${settled.length} selected event(s) — keeping the rest`,
              failed.map((r) => r.reason?.message || r.reason),
            );
          }
          const loaded = settled
            .filter((r) => r.status === 'fulfilled')
            .flatMap((r) => r.value);
          setCustomEvents(loaded);
        }
      } catch (error) {
        console.warn('Failed to load custom events for chart', error);
        if (isActive) {
          setCustomEvents([]);
        }
      }
    };

    fetchSelectedCustomEvents();
    return () => {
      isActive = false;
    };
  }, [formattedSymbol, symbolKeyVariants, customEventLookbackDays, selectedCustomEventIds]);


  useEffect(() => {
    let isActive = true;
    const fetchDefinitions = async () => {
      try {
        const response = await customEventsApi.listEvents({ limit: 200 });
        const items = Array.isArray(response?.events) ? response.events : response?.customEvents || [];
        if (isActive) {
          setCustomEventDefinitions(items);
          setCustomEventDefinitionsLoaded(true);
        }
      } catch (error) {
        console.warn('Failed to load custom event definitions', error);
        if (isActive) {
          setCustomEventDefinitions([]);
          // Deliberately leave `customEventDefinitionsLoaded` false on failure:
          // we couldn't confirm the definition set, so the chart must treat the
          // empty list as "unknown" (keep saved selections) rather than
          // "authoritatively empty" (which would wipe a user's valid selections
          // on a transient network blip). Pruning only happens after a
          // confirmed successful load.
        }
      }
    };
    fetchDefinitions();
    return () => {
      isActive = false;
    };
  }, []);

  // Cycle pivots come from the ledger's projection onto EventsTable and from
  // nowhere else; nothing in this page computes a pivot. Each absence is
  // stated on the chart by `describeCycleStatus`: a symbol with no backend
  // rows shows "no cycle data yet"; Tune shows the ledger's pivots while its
  // sliders move nothing — settings saved there apply on the next nightly run.
  const isTuningCycles = Boolean(localCycleLowSettings) || Boolean(localCycleHighSettings);
  const { events: keyLevelEvents, refetch: refetchKeyLevelEvents } = useBackendKeyLevelEvents(formattedSymbol);
  // Reported up by the chart, so stored cycle-fib events are fetched only while Fib Levels is on.
  const [fibShown, setFibShown] = useState(false);
  const { events: cycleFibEvents, refetch: refetchCycleFibEvents } = useBackendCycleFibEvents(formattedSymbol, { enabled: fibShown });
  // DWLF-338: daily rows, drawn only on the daily chart (behind the RSI / DSS events switches).
  const { events: divergenceEvents, refetch: refetchDivergenceEvents } = useBackendDivergenceEvents(formattedSymbol, {
    enabled: selectedTimeframe === 'daily',
  });
  const backendCycles = useBackendCycleEvents({
    symbol: formattedSymbol,
    timeframe: normalizedTimeframeKey,
  });
  // The STORED cycle window per side (DWLF-113 ①) — the chart draws exactly
  // these dates. No fallback: a side with no stored window gets no band and
  // the line above the chart says so.
  const storeTimeframe = isStoreTimeframe(normalizedTimeframeKey);
  // The stored window is a fact about today's chain, not about the Tune
  // panel's settings — so it stays through Tune.
  const showStoredWindows = storeTimeframe;
  const cycleWindows = useCycleWindows({
    symbol: formattedSymbol,
    timeframe: normalizedTimeframeKey,
    enabled: showStoredWindows,
  });
  // Past windows (DWLF-444): fetched only while the chart's layer is shown, in their own request.
  const [pastWindowsShown, setPastWindowsShown] = useState({ low: false, high: false });
  const anyPastWindowsShown = Object.values(pastWindowsShown).some(Boolean);
  const pastCycleWindows = usePastCycleWindows({
    symbol: formattedSymbol,
    timeframe: normalizedTimeframeKey,
    enabled: anyPastWindowsShown && showStoredWindows,
  });
  const pastWindowsStatus = describePastWindows({
    shown: pastWindowsShown,
    storeTimeframe: showStoredWindows,
    history: pastCycleWindows.history,
    loading: pastCycleWindows.loading,
    error: pastCycleWindows.error,
  });
  // Hourly: the store has no rows, so that chart keeps the pre-②a band — the
  // state row's quantiles, else the mean of visible pivots. Never fetched for
  // a store timeframe (weekly, daily).
  // Weinstein weekly stage (DWLF-382) for the chart's stage band, fetched only while the band is toggled on (it
  // defaults off); a failed fetch is said above the chart.
  const [stagesShown, setStagesShown] = useState(false);
  const weinsteinStage = useStage({ symbol: formattedSymbol, enabled: stagesShown });
  const cycleWindowState = useCycleWindowState({
    symbol: formattedSymbol,
    timeframe: normalizedTimeframeKey,
    enabled: !storeTimeframe,
  });
  // The line above the chart states what the record gave us and what is
  // not drawn, and why. Nothing is computed in its place. Windows are
  // stated for the store timeframes (weekly, daily); hourly keeps the legacy
  // band and says nothing about windows. The pivot part applies to every timeframe.
  const cycleWindowsStatus = describeCycleStatus({
    isTuningCycles,
    symbol: formattedSymbol,
    pivots: {
      loading: backendCycles.loading,
      error: backendCycles.error ? (backendCycles.error.message || String(backendCycles.error)) : null,
      emptyLow: backendCycles.liveLowEvents.length === 0,
      emptyHigh: backendCycles.liveHighEvents.length === 0,
    },
    windows: cycleWindows,
    storeTimeframe,
    depthRequested: cycleWindows.depthRequested === true,
    timeframeWord: bandTimeframe(normalizedTimeframeKey),
  });

  // The chart's notes (ChartStatusInfo, a marker in the plot's corner): only what is not drawn, or must be read with it.
  const statusLines = [
    { id: 'cycle-windows-status', text: cycleWindowsStatus, failed: cycleStatusFailed({ backendCycles, cycleWindows }) },
    { id: 'past-windows-status', text: pastWindowsStatus, failed: Boolean(pastCycleWindows.error) },
    { id: 'weinstein-stage-status', text: weinsteinStage.error ? `Weinstein stage unavailable (${weinsteinStage.error})` : null, failed: true },
  ].filter((line) => line.text);

  // One source for every cycle overlay: the backend hook. While the fetch is
  // in flight these are empty and the chart draws no pivots — never a
  // browser-computed substitute.
  const {
    weeklyCycleLows: effectiveWeeklyCycleLows,
    weeklyCycleHighs: effectiveWeeklyCycleHighs,
    liveCycleLowEvents: effectiveLiveCycleLowEvents,
    liveCycleHighEvents: effectiveLiveCycleHighEvents,
    liveCycleLow: effectiveLiveCycleLow,
    liveCycleHigh: effectiveLiveCycleHigh,
  } = useMemo(() => chartCycleProps({ backendCycles }), [backendCycles]);

  const swingPointsByTimeframe = useMemo(
    () => swingPointsState?.byTimeframe || {},
    [swingPointsState],
  );

  const swingPoints = useMemo(
    () =>
      swingPointsByTimeframe[selectedTimeframe]
      || swingPointsByTimeframe.daily
      || [],
    [swingPointsByTimeframe, selectedTimeframe],
  );

  // Reset strategy selection when switching trade types
  useEffect(() => {
    if (tradeSignalType === 'system') {
      setSelectedStrategy('all');
    }
  }, [tradeSignalType]);

  const supportedTimeframes = useMemo(() => {
    const defaults = { daily: true, weekly: true };
    const configured = symbolDetails?.supportedTimeframes;
    if (!configured) {
      return defaults;
    }
    return {
      ...defaults,
      ...configured,
    };
  }, [symbolDetails]);

  const timeframeOptions = useMemo(() => timeframeOptionsFor(supportedTimeframes), [supportedTimeframes]);

  const handleTimeframeChange = useCallback(
    (value) => {
      if (!value || value === selectedTimeframe) {
        return;
      }
      setSelectedTimeframe(value);
    },
    [selectedTimeframe],
  );

  // Handle query params for chart navigation from HistoricalFiresDialog
  useEffect(() => {
    if (!formattedSymbol || queryParamsConsumedRef.current) {
      return;
    }

    // Set timeframe from query param if valid
    const queryTimeframe = searchParams.get('timeframe');
    if (queryTimeframe && timeframeOptions.includes(queryTimeframe)) {
      setSelectedTimeframe(queryTimeframe);
      timeframeFromUrlRef.current = true;
    }

    // Scroll to date in chart if provided
    const queryDate = searchParams.get('date');
    if (queryDate && chartCandles && chartCandles.length > 0) {
      const targetDate = new Date(queryDate);
      if (!Number.isNaN(targetDate.getTime())) {
        const targetTimestamp = targetDate.getTime();
        
        // Find the candle index closest to the target date
        const targetIndex = chartCandles.findIndex((candle) => {
          const candleTime = candle?.t ?? candle?.timestamp ?? candle?.time ?? candle?.date;
          if (!candleTime) return false;
          const ts = typeof candleTime === 'number' ? candleTime : new Date(candleTime).getTime();
          return ts >= targetTimestamp;
        });

        if (targetIndex !== -1 && annotationChartRef.current) {
          // Use the chart's scrollToIndex method if available
          annotationChartRef.current.scrollToIndex?.(targetIndex);
          dateScrolledRef.current = true;
        }
      }
    }

    // Mark query params as consumed and clear them from URL to prevent feedback loop
    // Only mark as consumed after the date scroll has actually executed (if it was requested)
    const dateScrollNeeded = queryDate && chartCandles && chartCandles.length > 0;
    const dateScrollCompleted = dateScrollNeeded && dateScrolledRef.current;
    const canMarkConsumed = (queryTimeframe) || (queryDate && dateScrollCompleted);
    
    if (canMarkConsumed) {
      queryParamsConsumedRef.current = true;
      const newSearchParams = new URLSearchParams(searchParams);
      if (queryTimeframe) {
        newSearchParams.delete('timeframe');
      }
      if (queryDate) {
        newSearchParams.delete('date');
      }
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [formattedSymbol, searchParams, timeframeOptions, chartCandles, setSearchParams]);

  useEffect(() => {
    if (!formattedSymbol || !userSettingsLoaded) {
      return;
    }

    if (chartPreferencesReadyForSymbol) {
      return;
    }

    if (!timeframeOptions.length) {
      return;
    }

    // Legacy saved `trendlineVersion` / `trendlineVersionByTimeframe` (V2 era) are
    // dropped here so they are never applied and can never steer a trendline
    // request (DWLF-322). Another symbol's stored entry keeps them until that
    // symbol's preferences are next saved; the thunks ignore them regardless.
    // Cycle window toggles start from the pivot toggles they were split from (DWLF-440).
    const preferencesToApply = migrateCycleWindowPreferences(
      stripLegacyTrendlineVersionPreferences(savedChartPreferences),
    );

    updateChartPreferences(preferencesToApply, { markDirty: false });

    const preferredFromSettings = preferencesToApply.timeframe;
    // Skip preferences override if timeframe was set from URL query param
    if (!timeframeFromUrlRef.current) {
      if (
        preferredFromSettings &&
        timeframeOptions.includes(preferredFromSettings)
      ) {
        setSelectedTimeframe((current) =>
          current === preferredFromSettings ? current : preferredFromSettings,
        );
        setPendingPreferredTimeframe(null);
      } else if (preferredFromSettings) {
        setPendingPreferredTimeframe((prev) =>
          prev === preferredFromSettings ? prev : preferredFromSettings,
        );
      } else {
        setPendingPreferredTimeframe(null);
      }
    }

    if (
      preferencesToApply.tradeSignalType === 'system' ||
      preferencesToApply.tradeSignalType === 'user'
    ) {
      setTradeSignalType(preferencesToApply.tradeSignalType);
    }

    if (typeof preferencesToApply.selectedStrategy === 'string') {
      setSelectedStrategy(preferencesToApply.selectedStrategy);
    }

    setAppliedChartPreferencesSymbol(formattedSymbol);
  }, [
    formattedSymbol,
    userSettingsLoaded,
    chartPreferencesReadyForSymbol,
    timeframeOptions,
    savedChartPreferences,
    updateChartPreferences,
    setTradeSignalType,
    setSelectedStrategy,
    setAppliedChartPreferencesSymbol,
  ]);

  useEffect(() => {
    if (!pendingPreferredTimeframe) {
      return;
    }
    if (!timeframeOptions.includes(pendingPreferredTimeframe)) {
      return;
    }
    setSelectedTimeframe((current) =>
      current === pendingPreferredTimeframe
        ? current
        : pendingPreferredTimeframe,
    );
    setPendingPreferredTimeframe(null);
  }, [pendingPreferredTimeframe, timeframeOptions]);

  useEffect(() => {
    if (!pendingPreferredTimeframe) {
      return;
    }

    const supportKeyMap = {
      hourly: '1h',
      daily: 'daily',
      weekly: 'weekly',
    };

    const supportKey =
      supportKeyMap[pendingPreferredTimeframe] || pendingPreferredTimeframe;

    if (
      !Object.prototype.hasOwnProperty.call(supportedTimeframes, supportKey)
    ) {
      return;
    }

    if (timeframeOptions.includes(pendingPreferredTimeframe)) {
      return;
    }

    setPendingPreferredTimeframe(null);
  }, [pendingPreferredTimeframe, supportedTimeframes, timeframeOptions]);

  const trendlinesByTimeframe = trendlineState?.byTimeframe || {};

  const filterTrendlinesByTimeframe = useCallback((lines, timeframe) => {
    if (!Array.isArray(lines)) {
      return [];
    }

    const normalized =
      typeof timeframe === 'string' ? timeframe.toLowerCase() : '';

    return lines.filter((line) => {
      const lineTimeframe =
        typeof line?.timeframe === 'string'
          ? line.timeframe.toLowerCase()
          : undefined;

      if (!lineTimeframe) {
        return normalized === 'daily' || normalized === '';
      }

      return lineTimeframe === normalized;
    });
  }, []);

  const trendlinesByVersion = useMemo(() => {
    if (selectedTimeframe === 'weekly') {
      return weeklyTrendlineState?.versions || {};
    }
    if (!trendlineState) {
      return {};
    }
    return trendlinesByTimeframe[selectedTimeframe]?.versions || {};
  }, [
    trendlineState,
    selectedTimeframe,
    trendlinesByTimeframe,
    weeklyTrendlineState,
  ]);

  const dailyTrendlines = useMemo(() => {
    const versions = trendlinesByTimeframe.daily?.versions || {};
    const lines = versions[DEFAULT_TRENDLINE_VERSION] || [];
    return filterTrendlinesByTimeframe(lines, 'daily');
  }, [
    trendlinesByTimeframe,
    filterTrendlinesByTimeframe,
  ]);

  const hourlyTrendlines = useMemo(() => {
    const versions = trendlinesByTimeframe.hourly?.versions || {};
    const lines = versions[DEFAULT_TRENDLINE_VERSION] || [];
    return filterTrendlinesByTimeframe(lines, 'hourly');
  }, [
    trendlinesByTimeframe,
    filterTrendlinesByTimeframe,
  ]);

  const trendlines = useMemo(
    () =>
      filterTrendlinesByTimeframe(
        trendlinesByVersion[DEFAULT_TRENDLINE_VERSION] || [],
        selectedTimeframe,
      ),
    [
      selectedTimeframe,
      trendlinesByVersion,
      filterTrendlinesByTimeframe,
    ],
  );

  const weeklyTrendlines = useMemo(() => {
    const versions = weeklyTrendlineState?.versions || {};
    const lines = versions[DEFAULT_TRENDLINE_VERSION] || [];
    return filterTrendlinesByTimeframe(lines, 'weekly');
  }, [
    weeklyTrendlineState,
    filterTrendlinesByTimeframe,
  ]);

  const requestMarketData = useCallback(
    (timeframe, { force = false } = {}) => {
      if (!formattedSymbol) {
        return;
      }

      const normalizedTimeframe =
        typeof timeframe === 'string' ? timeframe.toLowerCase() : '';
      if (!normalizedTimeframe) {
        return;
      }

      const cachedCandles = marketData?.byTimeframe?.[normalizedTimeframe];
      if (force) {
        requestedTimeframesRef.current.delete(normalizedTimeframe);
      } else {
        if (Array.isArray(cachedCandles) && cachedCandles.length > 0) {
          return;
        }
        if (requestedTimeframesRef.current.has(normalizedTimeframe)) {
          return;
        }
      }

      requestedTimeframesRef.current.add(normalizedTimeframe);
      dispatch(
        loadMarketData({
          symbol: formattedSymbol,
          timeframe: normalizedTimeframe,
          semiLive:
            semiLiveCandlesEnabled &&
            (normalizedTimeframe === 'daily' || normalizedTimeframe === 'weekly'),
        }),
      );
    },
    [dispatch, formattedSymbol, marketData?.byTimeframe, semiLiveCandlesEnabled],
  );

  // The chart was panned back to its oldest candle: load the page before it (DWLF-426) and
  // redraw the trendlines over it (DWLF-434).
  const requestOlderCandles = useCallback(() => {
    if (!formattedSymbol || !normalizedTimeframeKey) {
      return;
    }
    dispatch(loadOlderHistory({ symbol: formattedSymbol, trendlineSymbol: symbol, timeframe: normalizedTimeframeKey }));
  }, [dispatch, formattedSymbol, symbol, normalizedTimeframeKey]);

  // Trendlines are always V1 (DWLF-322); the thunks pin the version themselves.
  const requestTrendlines = useCallback(
    (timeframe) => {
      if (!symbol) {
        return;
      }

      const normalizedTimeframe =
        typeof timeframe === 'string' ? timeframe.toLowerCase() : 'daily';

      if (requestedTrendlineTimeframesRef.current.has(normalizedTimeframe)) {
        return;
      }

      requestedTrendlineTimeframesRef.current.add(normalizedTimeframe);
      if (normalizedTimeframe === 'weekly') {
        dispatch(fetchWeeklyTrendlines({ symbol }));
      } else {
        dispatch(
          fetchTrendlines({
            symbol,
            timeframe: normalizedTimeframe,
          }),
        );
      }
    },
    [dispatch, symbol],
  );

  const fetchBaseData = useCallback(() => {
    if (!symbol) {
      return;
    }

    if (formattedSymbol) {
      // Key levels are one daily set per symbol, shown on every timeframe.
      dispatch(fetchSupportResistance({ symbol: formattedSymbol }));
      dispatch(
        fetchSwingPoints({ symbol: formattedSymbol, timeframe: 'daily' }),
      );
      dispatch(fetchSymbolDetails(formattedSymbol));
      dispatch(fetchWeeklySwingPoints(formattedSymbol));
    }

    requestTrendlines('weekly');
    dispatch(fetchAlertsByAsset(symbol));
    dispatch(fetchSystemFibLevels(symbol));
  }, [
    dispatch,
    formattedSymbol,
    symbol,
    requestTrendlines,
  ]);

  useEffect(() => {
    if (!symbol) {
      return;
    }
    fetchBaseData();
  }, [symbol, fetchBaseData]);

  useEffect(() => {
    previousTimeframeRef.current = null;
  }, [symbol]);

  useEffect(() => {
    if (!timeframeOptions.length || !chartPreferencesReadyForSymbol) {
      return;
    }

    if (
      pendingPreferredTimeframe &&
      !timeframeOptions.includes(pendingPreferredTimeframe)
    ) {
      return;
    }

    if (!timeframeOptions.includes(selectedTimeframe)) {
      if (
        pendingPreferredTimeframe &&
        timeframeOptions.includes(pendingPreferredTimeframe)
      ) {
        setSelectedTimeframe(pendingPreferredTimeframe);
        setPendingPreferredTimeframe(null);
        return;
      }
      setSelectedTimeframe(fallbackTimeframe(timeframeOptions));
    }
  }, [
    symbol,
    timeframeOptions,
    chartPreferencesReadyForSymbol,
    selectedTimeframe,
    pendingPreferredTimeframe,
  ]);

  useEffect(() => {
    if (!formattedSymbol || !selectedTimeframe) {
      return;
    }
    requestMarketData(selectedTimeframe);
  }, [formattedSymbol, selectedTimeframe, requestMarketData]);

  useEffect(() => {
    if (!formattedSymbol) {
      return;
    }
    timeframeOptions
      .filter((timeframe) => timeframe !== selectedTimeframe)
      .forEach((timeframe) => requestMarketData(timeframe));
  }, [formattedSymbol, selectedTimeframe, timeframeOptions, requestMarketData]);

  // Poll for fresh candles + backend cycle events every 15 minutes so a
  // user parked on the chart sees new hourly bars and pivots without
  // manually refreshing. The hourly lambda runs at :10 past the hour, so
  // a 15-minute cadence catches every new bar within ~5-15 min of the
  // lambda writing it. Daily/weekly get the same cadence — cheap, and
  // they auto-pick up their lambda's 03:00 UTC output whenever the next
  // poll lands after that.
  //
  // Paused while the tab is hidden (no burning API calls on a backgrounded
  // chart). Tune mode is fine — it saves settings, it does not change what
  // the chart reads. Refetch fires
  // immediately on tab-focus to cover the "tabbed away for an hour,
  // come back" case.
  const { refetch: refetchBackendCycles } = backendCycles;
  useEffect(() => {
    if (!formattedSymbol || !selectedTimeframe) {
      return undefined;
    }

    const POLLING_INTERVAL_MS = 15 * 60 * 1000;

    const refresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      requestMarketData(selectedTimeframe, { force: true });
      refetchBackendCycles();
      // Key levels and their events change once a night (03:20 UTC).
      dispatch(fetchSupportResistance({ symbol: formattedSymbol }));
      refetchKeyLevelEvents();
      refetchCycleFibEvents();
      refetchDivergenceEvents();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };

    const intervalId = setInterval(refresh, POLLING_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    formattedSymbol,
    selectedTimeframe,
    requestMarketData,
    refetchBackendCycles,
    refetchKeyLevelEvents,
    refetchCycleFibEvents,
    refetchDivergenceEvents,
    dispatch,
  ]);

  useEffect(() => {
    if (!selectedTimeframe) {
      return;
    }

    previousTimeframeRef.current = selectedTimeframe;
  }, [selectedTimeframe]);

  useEffect(() => {
    if (!formattedSymbol || !selectedTimeframe) {
      return;
    }

    if (selectedTimeframe === 'weekly') {
      dispatch(fetchWeeklySwingPoints(formattedSymbol));
      requestTrendlines('weekly');
      return;
    }

    dispatch(
      fetchSwingPoints({
        symbol: formattedSymbol,
        timeframe: selectedTimeframe,
      }),
    );
    requestTrendlines(selectedTimeframe);
  }, [
    dispatch,
    formattedSymbol,
    symbol,
    selectedTimeframe,
    requestTrendlines,
  ]);

  useEffect(() => {
    const handleHighlight = (e) => {
      setHighlightedPatterns(e.detail);
    };

    window.addEventListener('highlightPatterns', handleHighlight);
    return () =>
      window.removeEventListener('highlightPatterns', handleHighlight);
  }, []);


  const normalizedSymbols = useMemo(
    () => (Array.isArray(symbols) ? symbols.filter(Boolean) : []),
    [symbols],
  );

  const groupedSymbols = useMemo(() => {
    if (!normalizedSymbols.length) {
      return [];
    }

    const variantToCanonicalSymbol = normalizedSymbols.reduce((map, value) => {
      createSymbolVariants(value).forEach((variant) => {
        if (!map.has(variant)) {
          map.set(variant, value);
        }
      });
      return map;
    }, new Map());
    const watchlistSymbols = [];
    const canonicalToWatchlistLabel = new Map();
    const watchlistAssigned = new Set();
    if (Array.isArray(watchlist) && watchlist.length > 0) {
      const seenInWatchlist = new Set();
      watchlist.forEach((item) => {
        let symbolValue = null;
        if (typeof item === 'string') {
          symbolValue = item.trim();
        } else if (item && typeof item === 'object') {
          symbolValue =
            item.symbol || item.assetId || item.asset || item.id || null;
        }

        const trimmedSymbolValue =
          typeof symbolValue === 'string' ? symbolValue.trim() : '';
        const normalizedKey = normalizeSymbolKey(trimmedSymbolValue);
        const canonicalMatches = createSymbolVariants(trimmedSymbolValue)
          .map((variant) => variantToCanonicalSymbol.get(variant))
          .filter(Boolean);

        if (normalizedKey && canonicalMatches.length > 0 && !seenInWatchlist.has(normalizedKey)) {
          watchlistSymbols.push(trimmedSymbolValue);
          canonicalMatches.forEach((canonical) => {
            watchlistAssigned.add(canonical);
            if (!canonicalToWatchlistLabel.has(canonical)) {
              canonicalToWatchlistLabel.set(canonical, trimmedSymbolValue);
            }
          });
          seenInWatchlist.add(normalizedKey);
        }
      });
    }

    const assigned = new Set(watchlistAssigned);
    const groups = [];

    if (watchlistSymbols.length > 0) {
      groups.push({ tag: 'Watchlist', symbols: watchlistSymbols });
    }

    if (tagsLoaded) {
      tagsList.forEach((tag) => {
        const assets = assetsByTagMap?.[tag];
        if (!Array.isArray(assets) || assets.length === 0) {
          return;
        }

        const matched = [];
        const matchedSet = new Set();
        assets.forEach((asset) => {
          let assetSymbol = null;
          if (typeof asset === 'string') {
            assetSymbol = asset;
          } else if (asset && typeof asset === 'object') {
            assetSymbol =
              asset.symbol || asset.assetId || asset.asset || asset.id || null;
          }

          if (typeof assetSymbol !== 'string') {
            return;
          }

          const canonicalMatches = createSymbolVariants(assetSymbol)
            .map((variant) => variantToCanonicalSymbol.get(variant))
            .filter(Boolean);

          canonicalMatches.forEach((canonicalSymbol) => {
            if (!matchedSet.has(canonicalSymbol)) {
              matchedSet.add(canonicalSymbol);
              const displayLabel = canonicalToWatchlistLabel.get(canonicalSymbol) || canonicalSymbol;
              matched.push(displayLabel);
            }
          });
        });

        if (matched.length > 0) {
          matched.sort();
          groups.push({ tag, symbols: matched });
          matched.forEach((symbolValue) => assigned.add(symbolValue));
        }
      });
    }

    const others = normalizedSymbols.filter(
      (symbolValue) => !assigned.has(symbolValue),
    );
    if (others.length > 0) {
      groups.push({ tag: 'Others', symbols: [...others] });
    }

    return groups;
  }, [normalizedSymbols, tagsLoaded, tagsList, assetsByTagMap, watchlist]);

  const currentRouteSymbol = useMemo(
    () => formattedSymbol || symbol || '',
    [formattedSymbol, symbol],
  );

  const chartHasData = Array.isArray(chartCandles) && chartCandles.length > 0;
  const isInitialChartLoad = !chartHasData && status === 'loading';

  // Show contextual popover on first chart view
  // (Must be after chartHasData is defined to avoid TDZ reference error)
  useEffect(() => {
    const { show: chartPopoverShow, setAnchorEl: setChartPopoverAnchor } = chartPopover;
    if (!chartPopoverShow && symbolSidebarRef.current && chartHasData) {
      const timer = setTimeout(() => {
        setChartPopoverAnchor(symbolSidebarRef.current);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [chartHasData, chartPopover.show, chartPopover.setAnchorEl]);
  const chartViewportHeight = 'calc(100vh - 72px)';
  const chartAreaMinHeight = isMobile ? '65vh' : chartViewportHeight;
  const shouldShowLoadingSkeleton =
    userSettingsLoaded && !chartPreferencesReadyForSymbol;

  const handleSelectSymbol = useCallback(
    (nextSymbol) => {
      if (!nextSymbol) {
        return;
      }
      const cleaned = `${nextSymbol}`.trim();
      if (!cleaned) {
        return;
      }
      const routeSymbol = cleaned.replace('/', '-');
      if (routeSymbol === currentRouteSymbol) {
        return;
      }
      navigate(`/markets/${routeSymbol}`);
    },
    [navigate, currentRouteSymbol],
  );

  const handleAnnotationsChange = useCallback((annotations) => {
    setChartAnnotations(annotations || []);
  }, []);

  const handleSelectedAnnotationChange = useCallback((annotationId) => {
    setSelectedAnnotationId(annotationId);
  }, []);

  const handleAnnotationSelect = useCallback((annotation) => {
    if (!annotation || !annotationChartRef.current) {
      return;
    }
    
    // Select the annotation
    annotationChartRef.current.selectAnnotation(annotation);

    // Pan to center it
    annotationChartRef.current.scrollToAnnotation(annotation);

    // Open the edit/delete popover so list selection is actionable — the only
    // way to reach delete/edit for annotations that aren't easily clickable on
    // the chart (e.g. anchored_vwap, which renders as a series, not a hit-
    // testable annotation layer).
    annotationChartRef.current.openAnnotationPopover(annotation);
  }, []);

  const handleToggleAnnotationVisibility = useCallback((annotation) => {
    if (!annotation || !annotationChartRef.current) {
      return;
    }
    annotationChartRef.current.toggleAnnotationVisibility(annotation);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadWeeklyCycleLowSettings = async () => {
      try {
        // Fetch both new-schema (`indicators.cycleLow.<tf>.*`) and legacy
        // (`indicators.weeklyCycleLow.*`) prefixes in parallel, merge into a
        // single map. The library's buildWeeklyCycleLowSettings does dual-read
        // and expects both key shapes to be visible via getValue during the
        // migration window. After the one-off migration script runs and the
        // legacy keys are deleted, this second fetch will return empty and can
        // be retired in a follow-up.
        const [newSchema, legacySchema] = await Promise.all([
          getSystemSettings({ prefix: 'indicators.cycleLow.' }),
          getSystemSettings({ prefix: 'indicators.weeklyCycleLow.' }),
        ]);
        const settingsMap = {
          ...((legacySchema && (legacySchema.settings || legacySchema.data?.settings)) || {}),
          ...((newSchema && (newSchema.settings || newSchema.data?.settings)) || {}),
        };
        const resolved = buildWeeklyCycleLowSettings({
          getValue: (key) => settingsMap[key],
          symbol: formattedSymbol,
          timeframe: normalizedTimeframeKey,
        });
        if (!cancelled) {
          setWeeklyCycleLowSettings(resolved);
        }
      } catch (error) {
        if (!cancelled) {
          setWeeklyCycleLowSettings(null);
        }
        // Silently use defaults for 403 (non-admin users) — this is expected
        if (error?.response?.status !== 403) {
          // eslint-disable-next-line no-console
          console.warn('Failed to load weekly cycle-low settings; using defaults', error);
        }
      } finally {
        // Bump regardless of outcome so consumers (e.g. CycleTuningPanel
        // stale-detection) see the load round-trip completed even when
        // the value resolves to the same null state and React skips the
        // setWeekly… update.
        if (!cancelled) {
          setCycleLowLoadGeneration((g) => g + 1);
        }
      }
    };

    loadWeeklyCycleLowSettings();

    return () => {
      cancelled = true;
    };
  }, [formattedSymbol, normalizedTimeframeKey, cycleSettingsReloadToken]);

  useEffect(() => {
    let cancelled = false;

    const loadWeeklyCycleHighSettings = async () => {
      try {
        // See loadWeeklyCycleLowSettings for the dual-prefix rationale.
        const [newSchema, legacySchema] = await Promise.all([
          getSystemSettings({ prefix: 'indicators.cycleHigh.' }),
          getSystemSettings({ prefix: 'indicators.weeklyCycleHigh.' }),
        ]);
        const settingsMap = {
          ...((legacySchema && (legacySchema.settings || legacySchema.data?.settings)) || {}),
          ...((newSchema && (newSchema.settings || newSchema.data?.settings)) || {}),
        };
        const resolved = buildWeeklyCycleHighSettings({
          getValue: (key) => settingsMap[key],
          symbol: formattedSymbol,
          timeframe: normalizedTimeframeKey,
        });
        if (!cancelled) {
          setWeeklyCycleHighSettings(resolved);
        }
      } catch (error) {
        if (!cancelled) {
          setWeeklyCycleHighSettings(null);
        }
        // Silently use defaults for 403 (non-admin users) — this is expected
        if (error?.response?.status !== 403) {
          // eslint-disable-next-line no-console
          console.warn('Failed to load weekly cycle-high settings; using defaults', error);
        }
      } finally {
        // See loadWeeklyCycleLowSettings — bump the generation so consumers
        // detect load completion even when state stays at null.
        if (!cancelled) {
          setCycleHighLoadGeneration((g) => g + 1);
        }
      }
    };

    loadWeeklyCycleHighSettings();

    return () => {
      cancelled = true;
    };
  }, [formattedSymbol, normalizedTimeframeKey, cycleSettingsReloadToken]);

  if (shouldShowLoadingSkeleton) {
    return (
      <Box
        sx={{
          width: '100%',
          height: chartAreaMinHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <DWLFLoader size="large" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        width: '100%',
        height: { md: chartViewportHeight },
        minHeight: { md: chartViewportHeight },
        backgroundColor: 'background.default',
      }}
    >
      <Box
        sx={{
          flex: 1,
          // A flex item's default min-width is its content's: without 0, a chart canvas sized while the
          // symbols column was folded holds the row wide when it unfolds, pushing the Desk panel off-screen.
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: { md: chartViewportHeight },
          height: { md: chartViewportHeight },
        }}
      >
        <Box
          // Covers the chart plus the controls overlaid on it (debug, Tune,
          // Recompute) — same reason as the chart shell itself: a measure drag
          // must not start a text selection. See .dwlf-no-select in App.css.
          className="dwlf-no-select"
          sx={{
            // Stacked (phone) layout: not a growing flex item but a definite height (flex:1's zero basis
            // would override it).
            flex: isMobile ? 'none' : 1,
            position: 'relative',
            // Desktop: fills what the column leaves after the stage strip (a fixed min-height would push
            // the strip below the fold).
            minHeight: isMobile ? chartAreaMinHeight : 0,
            // Definite height on phones. With only a min-height the area grows to its
            // content, and the chart sizes itself to the area plus any fixed sub-pane (DSS adds 210px)
            // — a feedback loop that grew the chart without bound (DWLF-307).
            height: isMobile ? chartAreaMinHeight : undefined,
            backgroundColor: 'background.default',
          }}
        >
          <ChartStatusInfo lines={statusLines} />
          {(tuneButtonVisible || recomputeButtonVisible) && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 10,
                display: 'flex',
                gap: 1,
              }}
            >
              {tuneButtonVisible && (
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setCycleTuningOpen(true)}
                  sx={{
                    minWidth: 'auto',
                    py: 0,
                    px: 0.75,
                    fontSize: '0.7rem',
                    lineHeight: 1.4,
                    textTransform: 'none',
                  }}
                >
                  Tune
                </Button>
              )}
              {recomputeButtonVisible && (
                <Button
                  variant="text"
                  color="secondary"
                  onClick={handleRecomputeCycles}
                  disabled={recomputeInFlight}
                  sx={{
                    minWidth: 'auto',
                    py: 0,
                    px: 0.75,
                    fontSize: '0.7rem',
                    lineHeight: 1.4,
                    textTransform: 'none',
                  }}
                  title="Deletes this (symbol, timeframe)'s backend cycle state AND its stored events, then the next scheduled cron re-backfills from the batch detector. Pivots the ledger has retired come back visible. Use after a tune-save; you will be asked to confirm."
                >
                  {recomputeInFlight ? 'Recomputing…' : 'Recompute'}
                </Button>
              )}
            </Box>
          )}
          {isInitialChartLoad ? (
            <DWLFLoader size="large" />
          ) : chartHasData ? (
            <DWLFMarketChart
              isMobile={isMobile}
              title={title}
              symbol={symbol}
              marketData={chartCandles}
              onReachOldestCandles={requestOlderCandles}
              timeframe={selectedTimeframe}
              timeframeOptions={timeframeOptions}
              selectedTimeframe={selectedTimeframe}
              onTimeframeChange={handleTimeframeChange}
              supportResistance={supportResistance}
              keyLevelEvents={keyLevelEvents}
              cycleFibEvents={cycleFibEvents}
              divergenceEvents={divergenceEvents}
              trendlines={trendlines}
              dailyTrendlines={dailyTrendlines}
              hourlyTrendlines={hourlyTrendlines}
              weeklyTrendlines={weeklyTrendlines}
              chartPreferences={chartPreferences}
              axisColors={axisColors}
              crosshairSnapMode={crosshairSnapMode}
              showCrosshairPriceLabel={showCrosshairPriceLabel}
              showAdaptiveGrid={showAdaptiveGrid}
              onChartPreferencesChange={handleChartPreferencesChange}
              alerts={alerts}
              fibLevels={activeFibLevels}
              autoShowFib={autoShowFib}
              swingPoints={swingPoints}
              weeklySwingPoints={weeklySwingPoints}
              customEvents={customEvents}
              customEventDefinitions={customEventDefinitions}
              customEventDefinitionsLoaded={customEventDefinitionsLoaded}
              onSelectedCustomEventIdsChange={handleSelectedCustomEventIdsChange}
              systemTradeSignals={signals}
              userTradeSignals={userSignals}
              tradeJournalTrades={trades}
              showDailyTrendlines={showDailyTrendlines}
              showWeeklyTrendlines={showWeeklyTrendlines}
              showHourlyTrendlines={showHourlyTrendlines}
              onToggleDailyTrendlines={handleToggleDailyTrendlines}
              onToggleWeeklyTrendlines={handleToggleWeeklyTrendlines}
              onToggleHourlyTrendlines={handleToggleHourlyTrendlines}
              weeklyCycleLows={effectiveWeeklyCycleLows}
              cycleTrendlinesBeta={cycleTrendlinesBeta}
              weeklyCycleHighs={effectiveWeeklyCycleHighs}
              // Windows (weekly and daily, from the store) and the hourly legacy
              // band are facts about today's chain, not the Tune sliders — shown
              // in Tune as well.
              cycleWindows={showStoredWindows ? cycleWindows.windows : null}
              weinsteinStage={weinsteinStage.stage}
              onShowStagesChange={setStagesShown}
              onShowFibChange={setFibShown}
              pastCycleWindows={showStoredWindows ? pastCycleWindows.history : null}
              onShowPastWindowsChange={setPastWindowsShown}
              cycleWindowState={cycleWindowState.state}
              liveCycleLow={effectiveLiveCycleLow}
              liveCycleHigh={effectiveLiveCycleHigh}
              liveCycleLowEvents={effectiveLiveCycleLowEvents}
              liveCycleHighEvents={effectiveLiveCycleHighEvents}
              onAnnotationsChange={handleAnnotationsChange}
              onSelectedAnnotationChange={handleSelectedAnnotationChange}
              annotationRef={annotationChartRef}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
                px: 2,
              }}
            >
              <Typography variant="body1">No chart data available.</Typography>
            </Box>
          )}
        </Box>
        {!isMobile && (
          <StageStrip markets={clockMarkets} currentSymbol={currentRouteSymbol} onSelectSymbol={handleSelectSymbol} />
        )}
      </Box>
      {canTuneSystemSettings && formattedSymbol && (
        <CycleTuningPanel
          open={cycleTuningOpen}
          onClose={() => setCycleTuningOpen(false)}
          symbol={formattedSymbol}
          timeframe={normalizedTimeframeKey}
          lowLoadGeneration={cycleLowLoadGeneration}
          highLoadGeneration={cycleHighLoadGeneration}
          persistedLowSettings={weeklyCycleLowSettings}
          persistedHighSettings={weeklyCycleHighSettings}
          localLowSettings={localCycleLowSettings}
          localHighSettings={localCycleHighSettings}
          onLocalLowChange={setLocalCycleLowSettings}
          onLocalHighChange={setLocalCycleHighSettings}
          onSettingsChangedRemotely={() => setCycleSettingsReloadToken((t) => t + 1)}
        />
      )}
      <Box
        ref={symbolSidebarRef}
        sx={{
          // Collapsible on desktop to give the chart the room (a device preference, like the theme).
          // Width animates; the chart follows its container (useContainerSize), so it widens with it.
          position: 'relative',
          width: { xs: '100%', md: sidebarFolded ? SIDEBAR_RAIL : '15%' },
          maxWidth: { md: sidebarFolded ? SIDEBAR_RAIL : '15%' },
          flexBasis: { md: sidebarFolded ? SIDEBAR_RAIL : '15%' },
          minWidth: { md: sidebarFolded ? SIDEBAR_RAIL : 180 },
          transition: 'width 240ms cubic-bezier(0.2, 0, 0, 1), max-width 240ms cubic-bezier(0.2, 0, 0, 1), flex-basis 240ms cubic-bezier(0.2, 0, 0, 1), min-width 240ms cubic-bezier(0.2, 0, 0, 1)',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          overflow: { md: 'hidden' },
          flexShrink: 0,
          // The Desk (DWLF-300): symbols on the left of the chart, the symbol's panel on the right;
          // stacked on phones as chart → this symbol's panel → all symbols.
          order: { xs: 2, md: -1 },
          borderTop: { xs: '1px solid', md: 'none' },
          borderRight: { md: '1px solid' },
          borderColor: { md: 'var(--dwlf-ink-08)' },
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          height: { md: chartViewportHeight },
        }}
      >
        {!isMobile && (
          <SidebarToggle collapsed={sidebarFolded} onToggle={toggleSidebar} />
        )}
        <Box
          aria-hidden={sidebarFolded ? true : undefined}
          inert={sidebarFolded ? '' : undefined}
          sx={{
            display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
            // Fades before the column narrows, so the list never visibly reflows; held at its open
            // width while it does.
            minWidth: { md: 180 },
            opacity: sidebarFolded ? 0 : 1,
            transition: sidebarFolded ? 'opacity 120ms ease-out' : 'opacity 180ms ease-in 120ms',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
        <SymbolSidebar
          groupedSymbols={groupedSymbols}
          annotations={chartAnnotations}
          selectedAnnotationId={selectedAnnotationId}
          onSelectSymbol={handleSelectSymbol}
          onAnnotationSelect={handleAnnotationSelect}
          onToggleAnnotationVisibility={handleToggleAnnotationVisibility}
          loading={symbolsStatus !== 'succeeded' && groupedSymbols.length === 0}
          error={symbolsStatus === 'failed' && groupedSymbols.length === 0}
          errorMessage={symbolsError}
          retryCallback={() => dispatch(fetchMarketSymbols())}
          currentRouteSymbol={currentRouteSymbol}
          symbolCount={normalizedSymbols.length}
          sidebarTab={sidebarTab}
          setSidebarTab={setSidebarTab}
          collapsedAnnotationTypes={collapsedAnnotationTypes}
          setCollapsedAnnotationTypes={setCollapsedAnnotationTypes}
          stages={stagesBySymbol}
        />
        </Box>
      </Box>
      <Box
        sx={{
          width: { xs: '100%', md: 340 },
          order: 1,
          flexShrink: 0,
          borderTop: { xs: '1px solid var(--dwlf-ink-08)', md: 'none' },
          borderLeft: { md: '1px solid var(--dwlf-ink-08)' },
          backgroundColor: 'background.default',
          height: { md: chartViewportHeight },
        }}
      >
        <DeskPanel symbol={symbol} />
      </Box>

      <ContextualPopover
        open={chartPopover.show}
        anchorEl={chartPopover.anchorEl}
        onDismiss={chartPopover.dismiss}
        message="Switch symbols here or use overlay buttons to customize your chart view."
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
      <Snackbar
        open={recomputeSnack.open}
        onClose={() => setRecomputeSnack((prev) => ({ ...prev, open: false }))}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={recomputeSnack.severity}
          onClose={() => setRecomputeSnack((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {recomputeSnack.message}
        </Alert>
      </Snackbar>
      <Snackbar
        open={saveErrorOpen}
        onClose={closeSaveError}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={closeSaveError} sx={{ width: '100%' }}>
          Couldn&apos;t save your settings. Your latest change may be lost on reload.
        </Alert>
      </Snackbar>
      {/* (DWLF-104) The acknowledgement the backend requires. Spells out what
          is being undone rather than asking a generic "are you sure?" — the
          whole guard exists so this cannot happen unknowingly. */}
      <Dialog
        open={Boolean(bypassConfirmScope)}
        onClose={() => { if (!recomputeInFlight) { setBypassConfirmScope(null); } }}
      >
        <DialogTitle>Recompute bypasses the cycle ledger</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <p>
              Recompute deletes this scope&apos;s stored events and rebuilds them from the
              batch detector. A rebuilt row cannot carry <code>revoked</code>, so{' '}
              <strong>every pivot the ledger has retired for {bypassConfirmScope?.symbol} will come
              back visible</strong> — including to the strategies and evaluators that
              have been hiding them. This affects the{' '}
              <strong>{bypassConfirmScope?.tfKey}</strong> timeframe for that symbol only,
              both sides.
            </p>
            <p>
              If you are repairing something, prefer re-running the ledger for this
              scope. Use this only when you specifically want a rebuild from scratch,
              and re-run the ledger afterwards to re-assert revocation.
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBypassConfirmScope(null)} disabled={recomputeInFlight}>
            Cancel
          </Button>
          {/* Disabled while in flight: the dialog stays mounted and clickable
              through MUI's close transition, so a double-click would send a
              second rebuild. Cheap to guard, and this is not an action to send
              twice by accident. */}
          <Button
            onClick={handleRecomputeConfirmed}
            color="warning"
            variant="contained"
            disabled={recomputeInFlight || !bypassConfirmScope}
          >
            {recomputeInFlight ? 'Rebuilding…' : 'Rebuild anyway'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketDetail;
